import { Alert } from '@/components/ui/alert';
import { LoadingPage } from '@/components/ui/loading-page';
import { useAuth } from '@/contexts/auth-context';
import { useMcpCopublicityApprovalAction } from '@/hooks/actions/use-mcp-copublicity-action';
import {
  useMcpCopublicityOperationQuery,
  useMcpCopublicityReviewQuery,
} from '@/hooks/queries/use-mcp-copublicity-query';
import { isApiErrorCode } from '@/libs/api-error';
import { getMcpReturnTo } from '@/libs/mcp-return-to';
import { Box, Button, Flex, Stack } from '@chakra-ui/react';
import { ErrorCode } from '@fluenceur/types';
import { Logo } from '@fluenceur/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';
import { McpCopublicityReview } from './McpCopublicityReview';

function AccountReview({ id }: { id: string }) {
  const { t } = useTranslation();
  const query = useMcpCopublicityReviewQuery(id);
  const mutation = useMcpCopublicityApprovalAction(id);
  const [reviewVersion, setReviewVersion] = useState(0);
  const operation = useMcpCopublicityOperationQuery(query.data?.operationId ?? null);
  if (query.isPending) return <LoadingPage />;
  if (query.isError || !query.data)
    return (
      <Stack>
        {isApiErrorCode(query.error, ErrorCode.E_MCP_DISABLED) ? (
          <Alert status="warning" title={t('mcp.disabled')} />
        ) : isApiErrorCode(query.error, ErrorCode.E_MCP_TEMPORARILY_UNAVAILABLE) ? (
          <Alert status="warning" title={t('mcp.temporarilyUnavailable')} />
        ) : (
          <Alert status="error" title={t('mcp.copublicity.unavailable')} />
        )}
        <Button onClick={() => void query.refetch()}>{t('mcp.retry')}</Button>
      </Stack>
    );
  const refresh = async () => {
    const result = await query.refetch();
    if (result.isSuccess) setReviewVersion((version) => version + 1);
    if (query.data?.operationId) await operation.refetch();
  };
  return (
    <Stack>
      {operation.isError && <Alert status="error" title={t('mcp.copublicity.statusUnavailable')} />}
      <McpCopublicityReview
        key={`${query.data.preparation.version}:${query.data.operationId}:${reviewVersion}`}
        review={query.data}
        operation={operation.data}
        onApprove={(input) => mutation.mutateAsync(input)}
        onRefresh={() => void refresh()}
      />
    </Stack>
  );
}
export default function McpCopublicityReviewPage() {
  const { isAuthenticated, isLoading } = useAuth();
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const returnTo = getMcpReturnTo(`/mcp/copublicity/review/${id}`);
  if (isLoading) return <LoadingPage />;
  if (returnTo && !isAuthenticated)
    return <Navigate to={`/?${new URLSearchParams({ returnTo }).toString()}`} replace />;
  return (
    <Box minH="100vh" bg="bg.subtle" px={4} py={{ base: 6, md: 12 }}>
      <Flex mx="auto" mb={6} justify="center">
        <Logo width="143px" height="24px" />
      </Flex>
      <Box
        maxW="760px"
        mx="auto"
        bg="bg"
        borderWidth="1px"
        borderRadius="2xl"
        p={{ base: 5, md: 8 }}
      >
        {returnTo ? (
          <AccountReview id={id} />
        ) : (
          <Alert status="error" title={t('mcp.copublicity.unavailable')} />
        )}
      </Box>
    </Box>
  );
}
